import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllPages } from '../utils/fetchAllPages';

// --- Types ---

export interface DeviceTokenRecord {
  id: string;
  user_id: number;
  user?: { id: number; name: string; last_name: string; email: string };
}

/**
 * Active device tokens for the current business, expanded with the owning user.
 * Used to know which users are actually reachable by a push notification.
 *
 * NOTE: the `device_token` table is not documented in swagger-detailed.json and
 * carries no known `platform` (iOS/Android) column in the CRUD API today — see
 * SAM-264. Do not assume one exists without confirming with backend first.
 */
export const useDeviceTokenUsers = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['device-token-users'],
    // Every page: one page held only the first 500 tokens, so "Send to all" and the reachable counts fell short.
    queryFn: () => fetchAllPages<DeviceTokenRecord>('/device_token?expand=user,user_id&is_active=true', 500),
  });

  const devices = useMemo(() => data || [], [data]);

  // Deduplicate devices by user_id — a user can have more than one active token
  // (multiple installs/devices) but counts as a single reachable recipient.
  const uniqueUsers = useMemo(() => {
    const map = new Map<number, DeviceTokenRecord>();
    for (const d of devices) {
      if (!map.has(d.user_id)) map.set(d.user_id, d);
    }
    return Array.from(map.values());
  }, [devices]);

  const reachableUserIdSet = useMemo(() => new Set(uniqueUsers.map(u => u.user_id)), [uniqueUsers]);

  return { devices, uniqueUsers, reachableUserIdSet, isLoading };
};
